import { useSearchParams } from 'react-router-dom';

import { type TransactionsFilterInput, useGetTransactionsQuery } from 'data/__generated__';

import { DataTable } from 'components/DataTable';

import { transactionsColumns, type TransactionRow } from '../constants/transactionsColumns';

export const TransactionsList = () => {
    // Optional scope coming from a "voir plus" link (e.g. /transactions?userId=<id> on a user detail
    // page, or /transactions?siteId=<id> on a site detail page).
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('userId');
    const siteId = searchParams.get('siteId');
    const scopedFilters: Partial<TransactionsFilterInput> | undefined =
        userId || siteId ? { ...(userId ? { userId } : undefined), ...(siteId ? { siteId } : undefined) } : undefined;

    const {
        loading: transactionsLoading,
        data: transactionsData,
        error: transactionsError,
        refetch,
        fetchMore,
    } = useGetTransactionsQuery({
        // `sortBy` / `sortOrder` sont dans les `keyArgs` de la field policy `transactions` : les omettre ici
        // alors que le scroll infini les envoie ferait écrire `fetchMore` dans une AUTRE entrée de cache que
        // celle lue par le composant — pages chargées, rien qui s'ajoute. Ils doivent refléter le tri initial.
        variables: { filters: scopedFilters, sortBy: 'createdAt', sortOrder: 'desc' },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    if (transactionsError) {
        throw new Error('Une erreur est survenue lors de la récupération des transactions');
    }

    const hasMore = transactionsData?.transactions.hasMore ?? false;
    const transactions = transactionsData?.transactions.transactions ?? [];

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<TransactionRow, TransactionsFilterInput>
                columns={transactionsColumns}
                data={transactions}
                filterMode="server"
                // Scope du lien « voir plus » : il survit à « Effacer » et au rechargement de la page.
                pinnedFilters={scopedFilters}
                getRowId={(row) => row._id}
                getRowLink={(row) => `/transaction/${row._id}`}
                hasMore={hasMore}
                isLoading={transactionsLoading}
                refetch={refetch}
                fetchMore={fetchMore}
                defaultSortBy="createdAt"
                defaultSortOrder="desc"
                searchPlaceholder="Nom de l'utilisateur"
                searchFilterKey="searchValue"
            />
        </div>
    );
};
